import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { initAnalytics, trackPageView } from './utils/analytics';
import { initPlatform } from './utils/platform';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsRouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
}
// Route-lazy so the landing chunk (incl. gsap, and three via its own lazy
// import) never loads for users going straight into /app/*.
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage }))
);
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { PricingPage } from './pages/PricingPage';
import { UserGuidePage } from './pages/UserGuidePage';
import { NewResearch } from './pages/NewResearch';
import { JobDetail } from './pages/JobDetail';
import { ResearchLibrary } from './pages/ResearchLibrary';
import { SettingsPage } from './pages/SettingsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentViewPage } from './pages/DocumentViewPage';
import { MatterDetail } from './pages/MatterDetail';
import { TodayPage } from './pages/TodayPage';
import { CasesPage } from './pages/CasesPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetail } from './pages/ClientDetail';
import { TasksPage } from './pages/TasksPage';
import { DraftsPage } from './pages/DraftsPage';
import { NewDraftPage } from './pages/NewDraftPage';
import { DraftDetailPage } from './pages/DraftDetailPage';
import { Layout } from './components/layout/Layout';
import { ContentLibrary } from './pages/ContentLibrary';
import { ContentArticle } from './pages/ContentArticle';
import { FeedbackPage } from './pages/FeedbackPage';
import { PrivacyPage } from './pages/PrivacyPage';
// Dev-only design-review harness — route registered only when import.meta.env.DEV.
import { DevPreview } from './pages/DevPreview';

/**
 * Legacy-path redirect that carries the :id segment (and router state — list
 * pages pass seeds like `state.matter`) to its new canonical URL, so old
 * bookmarks and WhatsApp-sent links keep working (§4.0).
 */
function LegacyIdRedirect({ base }: { base: string }) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={`${base}/${id}`} replace state={location.state} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isCheckingAuth, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, []);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-legal-50 flex items-center justify-center">
        <div className="text-legal-500">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);
  useEffect(() => {
    initPlatform();
    initAnalytics();
    // Validate any persisted token on every page (not just /app/*), so a stale/
    // expired token is cleared and the public nav correctly shows "Sign In"
    // instead of "Dashboard". The optimistic isAuthenticated keeps valid users
    // from flashing a logout on protected-route refresh.
    checkAuth();
  }, [checkAuth]);
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <Routes>
        <Route
          path="/"
          element={
            <Suspense fallback={<div className="min-h-screen bg-legal-900" />}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/user-guide" element={<UserGuidePage />} />
        <Route path="/learn" element={<ContentLibrary />} />
        <Route path="/learn/:slug" element={<ContentArticle />} />
        <Route path="/compare/:slug" element={<ContentArticle />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {import.meta.env.DEV && <Route path="/dev/preview" element={<DevPreview />} />}
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Home — Today (spec §3): the morning cause list + recent work */}
          <Route index element={<TodayPage />} />
          <Route path="research" element={<ResearchLibrary />} />
          <Route path="research/new" element={<NewResearch />} />
          {/* Memo tabs are URL-driven (§4.3): memo | discuss | quiz | opposing | drafts.
              Single route with an optional segment so tab switches never remount
              JobDetail (a remount would refetch the full memo blob). */}
          <Route path="research/:id/:tab?" element={<JobDetail />} />
          <Route path="drafts" element={<DraftsPage />} />
          <Route path="drafts/new" element={<NewDraftPage />} />
          <Route path="drafts/:id" element={<DraftDetailPage />} />
          <Route path="cases" element={<CasesPage />} />
          <Route path="cases/:id" element={<MatterDetail />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="documents/:id" element={<DocumentViewPage />} />

          {/* ---- Legacy routes (§4.0) — old paths live forever via redirects ---- */}
          <Route path="practice" element={<Navigate to="/app" replace />} />
          <Route path="practice/cases" element={<Navigate to="/app/cases" replace />} />
          <Route path="practice/cases/:id" element={<LegacyIdRedirect base="/app/cases" />} />
          <Route path="practice/clients" element={<Navigate to="/app/clients" replace />} />
          <Route path="practice/clients/:id" element={<LegacyIdRedirect base="/app/clients" />} />
          <Route path="practice/tasks" element={<Navigate to="/app/tasks" replace />} />
          <Route path="matters" element={<Navigate to="/app/cases" replace />} />
          <Route path="matters/:id" element={<LegacyIdRedirect base="/app/cases" />} />
          {/* /app/pricing was never a registered route — send purchase intent to the real page */}
          <Route path="pricing" element={<Navigate to="/pricing" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
